import { useState, useEffect } from "react";
import { Check, Zap, Star, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

const PLANS = [
  {
    id: "free",
    name: "Gratuito",
    icon: null,
    priceMonthly: 0,
    priceYearly: 0,
    color: "border-border",
    features: ["3 anúncios por mês", "Busca no mapa", "Mensagens ilimitadas"],
    priceIdMonthly: null,
    priceIdYearly: null,
  },
  {
    id: "plus",
    name: "Plus",
    icon: Zap,
    priceMonthly: 19.90,
    priceYearly: 16.92,
    color: "border-blue-400",
    highlight: false,
    features: ["10 anúncios por mês", "2 destaques", "Busca no mapa", "Mensagens ilimitadas"],
    priceIdMonthly: "price_1U8pesERqy2wI8wcdJsUTPH3",
    priceIdYearly: "price_1U8pgRERqy2wI8wcdPsyLHHn",
  },
  {
    id: "pro",
    name: "Pro",
    icon: Star,
    priceMonthly: 29.90,
    priceYearly: 25.42,
    color: "border-primary",
    highlight: true,
    features: ["25 anúncios por mês", "10 destaques", "Badge verificado", "Busca no mapa", "Mensagens ilimitadas"],
    priceIdMonthly: "price_1U8ph7ERqy2wI8wc0XdCLVLL",
    priceIdYearly: "price_1U8piAERqy2wI8wcrqsIL80I",
  },
  {
    id: "ultra",
    name: "Ultra",
    icon: Crown,
    priceMonthly: 39.90,
    priceYearly: 33.92,
    color: "border-yellow-400",
    highlight: false,
    features: ["Anúncios ilimitados", "30 destaques", "Badge verificado", "Suporte prioritário", "Busca no mapa"],
    priceIdMonthly: "price_1U8pisERqy2wI8wcySq15dD2",
    priceIdYearly: "price_1U8pjKERqy2wI8wcCJxIdLJN",
  },
];

export default function PlansPage() {
  const { user } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("success") !== "true") return;
    toast.success("🎉 Plano ativado com sucesso! Seus novos recursos já estão disponíveis.", { duration: 6000 });
    // avisa o Navbar para recarregar o plano (badge) sem F5, com alguns retries
    // enquanto o webhook do Stripe termina de processar
    let tries = 0;
    const iv = setInterval(() => {
      window.dispatchEvent(new Event("plan-updated"));
      if (++tries >= 5) clearInterval(iv);
    }, 2000);
    // limpa o parâmetro da URL
    searchParams.delete("success");
    setSearchParams(searchParams, { replace: true });
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState<string | null>(null);

  async function handleSubscribe(plan: typeof PLANS[0]) {
    if (!user) { navigate("/login"); return; }
    if (plan.id === "free") return;

    const priceId = billing === "monthly" ? plan.priceIdMonthly : plan.priceIdYearly;
    if (!priceId) {
      alert("Pagamentos em breve disponíveis!");
      return;
    }

    setLoading(plan.id);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { priceId, userId: user.id, email: user.email, billingCycle: billing, affiliateRef: localStorage.getItem("affiliateRef") || null },
      });
      if (error) throw error;
      window.location.href = data.url;
    } catch (err) {
      alert("Erro ao processar pagamento. Tente novamente.");
    }
    setLoading(null);
  }

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold mb-4">Planos AgroAluga</h1>
          <p className="text-muted-foreground text-lg mb-8">Escolha o plano ideal para o seu negócio agrícola</p>
          
          {/* Toggle mensal/anual */}
          <div className="inline-flex items-center gap-3 bg-secondary rounded-full p-1">
            <button onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billing === "monthly" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
              Mensal
            </button>
            <button onClick={() => setBilling("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billing === "yearly" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
              Anual <span className="text-xs text-primary font-bold ml-1">-15%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const price = billing === "monthly" ? plan.priceMonthly : plan.priceYearly;
            return (
              <div key={plan.id} className={`relative bg-card border-2 ${plan.color} rounded-2xl p-6 flex flex-col ${plan.highlight ? "shadow-lg scale-105" : ""}`}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1 rounded-full">
                    Mais popular
                  </div>
                )}
                <div className="flex items-center gap-2 mb-4">
                  {Icon && <Icon className="h-5 w-5 text-primary" />}
                  <h2 className="text-xl font-heading font-bold">{plan.name}</h2>
                </div>
                <div className="mb-6">
                  {price === 0 ? (
                    <p className="text-3xl font-bold">Grátis</p>
                  ) : (
                    <div>
                      <span className="text-3xl font-bold">R$ {price.toFixed(2).replace(".", ",")}</span>
                      <span className="text-muted-foreground text-sm">/mês</span>
                      {billing === "yearly" && (
                        <p className="text-xs text-primary mt-1">Cobrado anualmente</p>
                      )}
                    </div>
                  )}
                </div>
                <ul className="space-y-3 flex-1 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(plan)}
                  disabled={loading === plan.id || plan.id === "free"}
                  className={`w-full ${plan.highlight ? "bg-primary text-primary-foreground" : "variant-outline"}`}
                  variant={plan.highlight ? "default" : "outline"}
                >
                  {loading === plan.id ? "Aguarde..." : plan.id === "free" ? "Plano atual" : "Assinar"}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Pagamentos processados com segurança pelo Stripe. Cancele a qualquer momento.
        </p>
      </div>
    </div>
  );
}
